/**
 * PautaSlot — renderiza pautas publicitarias activas para una posición.
 *
 * Uso:
 *   <PautaSlot posicion="home_top" />
 *   <PautaSlot posicion="entre_articulos" edicionId="uuid" />
 *   <PautaSlot posicion="sidebar" />
 *
 * Si no hay pautas activas no renderiza nada — sin espacio vacío.
 */

import { useState, useEffect } from 'react';
import { publicidadService } from '../services/api';

const SLOT_STYLES = {
  home_top: {
    wrapper: {
      width: '100%',
      background: 'transparent',
      borderBottom: '1px solid #e8e8e4',
      padding: '10px 0 6px',
    },
    inner: { width: '100%', maxWidth: 1200, margin: '0 auto', textAlign: 'center', padding: '0 24px', boxSizing: 'border-box' },
  },
  entre_articulos: {
    wrapper: {
      width: '100%',
      background: '#f8f8f6',
      borderTop: '1px solid #e8e8e4',
      borderBottom: '1px solid #e8e8e4',
      padding: '10px 0 6px',
      margin: '24px 0',
    },
    inner: { width: '100%', maxWidth: 1200, margin: '0 auto', textAlign: 'center', padding: '0 24px', boxSizing: 'border-box' },
  },
  sidebar: {
    wrapper: { marginBottom: 24 },
    inner: { maxWidth: 300, margin: '0 auto', textAlign: 'center' },
  },
  in_content: {
    wrapper: {
      float: 'right',
      margin: '0 0 16px 24px',
      clear: 'right',
    },
    inner: { maxWidth: 300, textAlign: 'center' },
  },
  footer: {
    wrapper: {
      width: '100%',
      background: '#f0f0ee',
      borderTop: '1px solid #e0e0dc',
      padding: '10px 0 6px',
    },
    inner: { maxWidth: 728, margin: '0 auto', textAlign: 'center' },
  },
  revista_pagina: {
    wrapper: { width: '100%', textAlign: 'center' },
    inner: { maxWidth: '100%' },
  },
  revista_entre_paginas: {
    wrapper: { width: '100%', textAlign: 'center', padding: '8px 0' },
    inner: { maxWidth: '100%' },
  },
};

const DEFAULT_STYLE = {
  wrapper: { width: '100%', padding: '12px 0 8px', textAlign: 'center' },
  inner: { maxWidth: 728, margin: '0 auto' },
};

export default function PautaSlot({ posicion, edicionId = null, style = {} }) {
  const [pautas, setPautas] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    publicidadService.getPautasActivas(posicion, edicionId)
      .then(data => { if (!cancelled) setPautas(data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [posicion, edicionId]);

  useEffect(() => {
    if (pautas.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % pautas.length), 8000);
    return () => clearInterval(t);
  }, [pautas.length]);

  // Registrar impresión cada vez que la pauta visible cambia
  useEffect(() => {
    const activa = pautas[idx];
    if (!activa) return;
    publicidadService.registrarEvento?.(activa.id, { materialId: null, edicionId, tipo: 'impresion' })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, pautas]);

  if (pautas.length === 0) return null;

  const slotStyle = SLOT_STYLES[posicion] || DEFAULT_STYLE;

  const handleClickFor = (p) => () => {
    publicidadService.registrarEvento?.(p.id, { materialId: null, edicionId, tipo: 'clic' })
      .catch(() => {});
    if (p.urlDestino) window.open(p.urlDestino, '_blank', 'noopener');
  };

  const renderMaterial = (p) => {
    const imgStyle = {
      display: 'block',
      margin: '0 auto',
      cursor: p.urlDestino ? 'pointer' : 'default',
      maxWidth: '100%',
      width: p.anchoPx ? `${p.anchoPx}px` : 'auto',
      height: p.altoPx ? `${p.altoPx}px` : 'auto',
    };

    if (p.tipoArchivo === 'imagen') {
      return (
        <img
          src={p.urlStorage}
          alt="Publicidad"
          onClick={p.urlDestino ? handleClickFor(p) : undefined}
          style={imgStyle}
        />
      );
    }
    if (p.tipoArchivo === 'video') {
      return (
        <video
          src={p.urlStorage}
          autoPlay muted loop playsInline
          onClick={p.urlDestino ? handleClickFor(p) : undefined}
          style={{ ...imgStyle, cursor: p.urlDestino ? 'pointer' : 'default' }}
        />
      );
    }
    if (p.tipoArchivo === 'audio') {
      return (
        <audio src={p.urlStorage} controls
          style={{ width: '100%', maxWidth: 500 }} />
      );
    }
    if (p.tipoArchivo === 'html') {
      return (
        <div dangerouslySetInnerHTML={{ __html: p.urlStorage }}
          style={{ width: '100%' }} />
      );
    }
    return null;
  };

  return (
    <div style={{ ...slotStyle.wrapper, ...style }}>
      <div style={slotStyle.inner}>
        {/* Label "Publicidad" — obligatorio por normativa SIC */}
        <div style={{
          fontSize: 9, color: '#aaa', letterSpacing: '1.5px',
          textTransform: 'uppercase', marginBottom: 4, fontFamily: 'sans-serif',
        }}>
          Publicidad
        </div>

        {/* Track del carrusel — desliza horizontalmente entre pautas */}
        <div style={{ overflow: 'hidden', width: '100%' }}>
          <div style={{
            display: 'flex',
            transition: 'transform 600ms ease-in-out',
            transform: `translateX(-${idx * 100}%)`,
          }}>
            {pautas.map((p, i) => (
              <div key={p.id ?? i} style={{ flex: '0 0 100%', minWidth: 0 }}>
                {renderMaterial(p)}
              </div>
            ))}
          </div>
        </div>

        {/* Dots de rotación */}
        {pautas.length > 1 && (
          <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginTop: 6 }}>
            {pautas.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} style={{
                width: 6, height: 6, borderRadius: '50%', border: 'none',
                padding: 0, background: i === idx ? '#1B4332' : '#ccc',
                cursor: 'pointer',
              }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
